(ns ductile.ui.load-builder-v2.reducer
  (:require [clojure.string :as str]
            [ductile.ui.load-builder-v2.selectors :as lb2sel]
            [ductile.ui.load-builder-v2.state :as lb2state]))

(def managed-keys
  #{:layout
    :popouts
    :draft-transports
    :draft-order
    :transport->vehicle-ids
    :transport->waypoint-settings
    :staging-ids
    :focused-transport-id
    :focused-selection-vehicle-ids
    :focused-route
    :focused-route-fit-key
    :route-loading?
    :route-loading-transport-id
    :pending-ops
    :menu
    :selected-vehicle-ids
    :selection-anchor-id
    :disconnected?
    :editing-transport-id
    :show-cols-modal?
    :views
    :selected-view-id
    :view-baseline-snapshot})

(defn create-draft-id []
  (keyword (str "draft-" (random-uuid))))

(defn ensure-draft-order [ids draft-id]
  (let [ids' (vec ids)]
    (if (some #{draft-id} ids')
      ids'
      (conj ids' draft-id))))

(defn- detach-vehicles [transport->vehicle-ids vehicle-ids]
  (reduce (fn [acc vid]
            (if-let [src (lb2sel/vehicle-source-transport-id acc vid)]
              (update acc src (fn [ids] (vec (remove #{vid} ids))))
              acc))
          transport->vehicle-ids
          vehicle-ids))

(defn- remove-from-staging [staging-ids vehicle-ids]
  (vec (remove (set vehicle-ids) staging-ids)))

(defn- distinct-filtered-vec [pred xs]
  (->> (or xs [])
       (filter pred)
       distinct
       vec))

(defn- normalize-view-snapshot [snapshot]
  {:col-settings (:col-settings snapshot)
   :filters (vec (or (:filters snapshot) []))})

(defn- normalize-view [{:keys [id name snapshot]}]
  (when-let [name' (lb2sel/normalize-view-name name)]
    {:id (or id (random-uuid))
     :name name'
     :snapshot (normalize-view-snapshot snapshot)}))

(defn- loads->signatures [loads]
  (into {}
        (map (fn [load]
               [(lb2sel/load-key load)
                (hash load)]))
        (or loads [])))

(defn init-state [{:keys [loads fahrzeuge layout shared]}]
  (let [base {:layout layout
              :popouts {}
              :draft-transports {}
              :draft-order []
              :transport->vehicle-ids (lb2sel/loads->assignment loads fahrzeuge)
              :transport->waypoint-settings {}
              :staging-ids []
              :focused-transport-id nil
              :focused-selection-vehicle-ids []
              :focused-route nil
              :focused-route-fit-key nil
              :route-loading? false
              :route-loading-transport-id nil
              :pending-ops {}
              :transport-update-tokens {}
              :server-load-signatures (loads->signatures loads)
              :menu nil
              :selected-vehicle-ids #{}
              :selection-anchor-id nil
              :disconnected? false
              :editing-transport-id nil
              :show-cols-modal? false
              :views []
              :selected-view-id nil
              :view-baseline-snapshot nil}]
    (merge base (select-keys shared managed-keys))))

(defn reduce-event [state {:keys [event] :as payload}]
  (case event
    :layout/set
    (assoc state :layout (:layout payload))

    :layout/update
    (update state :layout (:f payload identity))

    :popouts/set
    (assoc state :popouts (:popouts payload))

    :popouts/update
    (update state :popouts (:f payload identity))

    :selection/click
    (let [{:keys [selected-ids anchor-id]}
          (lb2state/click-selection
           {:selected-ids (:selected-vehicle-ids state)
            :anchor-id (:selection-anchor-id state)}
           {:row-id (:row-id payload)
            :ordered-ids (:ordered-ids payload)
            :toggle? (:toggle? payload)
            :shift? (:shift? payload)})
          focused-selection? (seq (:focused-selection-vehicle-ids state))
          state' (assoc state
                        :selected-vehicle-ids selected-ids
                        :selection-anchor-id anchor-id)]
      (if focused-selection?
        (assoc state'
               :focused-selection-vehicle-ids (vec selected-ids)
               :focused-route nil
               :focused-route-fit-key nil
               :route-loading? false
               :route-loading-transport-id nil
               :focused-transport-id nil)
        state'))

    :selection/drag-preview
    ;; Kept for state-machine parity; current UI does not render preview-only state.
    state

    :selection/drag-commit
    (let [{:keys [selected-ids anchor-id]}
          (lb2state/drag-selection
           {:selected-ids (:selected-vehicle-ids state)
            :anchor-id (:selection-anchor-id state)}
           {:row-keys (:row-keys payload)
            :append? (:append? payload)})
          focused-selection? (seq (:focused-selection-vehicle-ids state))
          state' (assoc state
                        :selected-vehicle-ids selected-ids
                        :selection-anchor-id anchor-id)]
      (if focused-selection?
        (assoc state'
               :focused-selection-vehicle-ids (vec selected-ids)
               :focused-route nil
               :focused-route-fit-key nil
               :route-loading? false
               :route-loading-transport-id nil
               :focused-transport-id nil)
        state'))

    :selection/clear
    (cond-> (assoc state :selected-vehicle-ids #{} :selection-anchor-id nil)
      (seq (:focused-selection-vehicle-ids state))
      (assoc :focused-selection-vehicle-ids []
             :focused-route nil
             :focused-route-fit-key nil
             :route-loading? false
             :route-loading-transport-id nil))

    :menu/set
    (assoc state :menu (:menu payload))

    :menu/clear
    (assoc state :menu nil)

    :ui/set-disconnected
    (assoc state :disconnected? (boolean (:disconnected? payload)))

    :ui/set-editing-transport
    (assoc state :editing-transport-id (:transport-id payload))

    :ui/set-show-cols-modal
    (assoc state :show-cols-modal? (boolean (:show? payload)))

    :views/init
    (let [views-state (:views-state payload)
          views (vec (keep normalize-view (:views views-state)))
          selected-view-id (:selected-view-id views-state)
          selected-view-id (when (some #(= selected-view-id (:id %)) views)
                             selected-view-id)
          baseline (some-> (lb2sel/current-view views selected-view-id) :snapshot)]
      (assoc state
             :views views
             :selected-view-id selected-view-id
             :view-baseline-snapshot baseline))

    :views/select
    (let [selected-view-id (:selected-view-id payload)]
      (if-let [view (lb2sel/current-view (:views state) selected-view-id)]
        (assoc state
               :selected-view-id (:id view)
               :view-baseline-snapshot (:snapshot view))
        (assoc state
               :selected-view-id nil
               :view-baseline-snapshot nil)))

    :views/save-new
    (let [name' (lb2sel/normalize-view-name (:name payload))
          snapshot (normalize-view-snapshot (:snapshot payload))
          view-id (or (:id payload) (random-uuid))]
      (if (and name' (lb2sel/name-available? (:views state) name'))
        (-> state
            (update :views conj {:id view-id
                                 :name name'
                                 :snapshot snapshot})
            (assoc :selected-view-id view-id
                   :view-baseline-snapshot snapshot))
        state))

    :views/save-current
    (if-let [selected-view-id (:selected-view-id state)]
      (if-let [snapshot (:snapshot payload)]
        (let [snapshot' (normalize-view-snapshot snapshot)]
          (-> state
              (update :views (fn [views]
                               (mapv (fn [view]
                                       (if (= selected-view-id (:id view))
                                         (assoc view :snapshot snapshot')
                                         view))
                                     views)))
              (assoc :view-baseline-snapshot snapshot')))
        state)
      state)

    :views/rename
    (let [view-id (:view-id payload)
          name' (lb2sel/normalize-view-name (:name payload))]
      (if (and name' (lb2sel/name-available? (:views state) name' view-id))
        (update state :views (fn [views]
                               (mapv (fn [view]
                                       (if (= view-id (:id view))
                                         (assoc view :name name')
                                         view))
                                     views)))
        state))

    :views/duplicate
    (let [source-id (:source-id payload)
          source (lb2sel/current-view (:views state) source-id)
          name' (lb2sel/normalize-view-name (:name payload))
          view-id (or (:id payload) (random-uuid))]
      (if (and source name' (lb2sel/name-available? (:views state) name'))
        (-> state
            (update :views conj {:id view-id
                                 :name name'
                                 :snapshot (:snapshot source)})
            (assoc :selected-view-id view-id
                   :view-baseline-snapshot (:snapshot source)))
        state))

    :views/delete
    (let [view-id (:view-id payload)
          selected? (= view-id (:selected-view-id state))
          views' (vec (remove #(= view-id (:id %)) (:views state)))]
      (cond-> (assoc state :views views')
        selected? (assoc :selected-view-id nil
                         :view-baseline-snapshot nil)))

    :views/discard-changes
    (if-let [view (lb2sel/current-view (:views state) (:selected-view-id state))]
      (assoc state :view-baseline-snapshot (:snapshot view))
      state)

    :transport/move-to-transport
    (let [vehicle-ids (vec (:vehicle-ids payload))
          target-id (:target-id payload)
          detached (detach-vehicles (:transport->vehicle-ids state) vehicle-ids)
          current-target (or (get detached target-id) [])
          target-with-vehicles (->> (concat current-target vehicle-ids) distinct vec)]
      (-> state
          (assoc-in [:transport->vehicle-ids target-id] target-with-vehicles)
          (update :staging-ids remove-from-staging vehicle-ids)))

    :transport/move-to-staging
    (let [vehicle-ids (vec (:vehicle-ids payload))
          detached (detach-vehicles (:transport->vehicle-ids state) vehicle-ids)]
      (-> state
          (assoc :transport->vehicle-ids detached)
          (update :staging-ids (fn [ids]
                                 (->> (concat (or ids []) vehicle-ids)
                                      distinct
                                      vec)))))

    :transport/remove-from-staging
    (let [vehicle-ids (vec (:vehicle-ids payload))]
      (update state :staging-ids remove-from-staging vehicle-ids))

    :transport/remove-from-load
    (let [vehicle-ids (vec (:vehicle-ids payload))
          detached (detach-vehicles (:transport->vehicle-ids state) vehicle-ids)]
      (assoc state :transport->vehicle-ids detached))

    :transport/clear-staging
    (assoc state :staging-ids [])

    :draft/create
    (let [draft-id (or (:draft-id payload) (create-draft-id))
          vehicle-ids (vec (:vehicle-ids payload))
          draft {:transport/kind :transport.kind/draft
                 :draft? true
                 :ductile/id draft-id}]
      (-> state
          (assoc-in [:draft-transports draft-id] draft)
          (update :draft-order ensure-draft-order draft-id)
          (assoc-in [:transport->vehicle-ids draft-id] vehicle-ids)))

    :draft/create-with-vehicles
    (let [draft-id (or (:draft-id payload) (create-draft-id))
          vehicle-ids (vec (:vehicle-ids payload))
          draft {:transport/kind :transport.kind/draft
                 :draft? true
                 :ductile/id draft-id}
          detached (detach-vehicles (:transport->vehicle-ids state) vehicle-ids)]
      (-> state
          (assoc :transport->vehicle-ids (assoc detached draft-id vehicle-ids))
          (update :staging-ids remove-from-staging vehicle-ids)
          (assoc-in [:draft-transports draft-id] draft)
          (update :draft-order ensure-draft-order draft-id)))

    :draft/dismiss
    (let [transport-id (:transport-id payload)]
      (cond-> (-> state
                  (update :draft-transports dissoc transport-id)
                  (update :draft-order (fn [ids] (vec (remove #{transport-id} ids))))
                  (update :transport->vehicle-ids dissoc transport-id)
                  (update :transport->waypoint-settings dissoc transport-id))
        (= (:focused-transport-id state) transport-id)
        (assoc :focused-transport-id nil
               :focused-selection-vehicle-ids []
               :focused-route nil
               :focused-route-fit-key nil
               :route-loading? false
               :route-loading-transport-id nil)))

    :transport/set-waypoint-settings
    (let [transport-id (:transport-id payload)
          settings {:optimized-route? (boolean (:optimized-route? payload))
                    :waypoint-order (vec (or (:waypoint-order payload) []))}]
      (if transport-id
        (assoc-in state [:transport->waypoint-settings transport-id] settings)
        state))

    :draft/rename
    (assoc-in state [:draft-transports (:transport-id payload) :draft/name]
              (not-empty (str/trim (or (:name payload) ""))))

    :route/hide
    (assoc state
           :focused-transport-id nil
           :focused-selection-vehicle-ids []
           :focused-route nil
           :focused-route-fit-key nil
           :route-loading? false
           :route-loading-transport-id nil)

    :route/start
    (assoc state
           :focused-transport-id (:transport-id payload)
           :focused-selection-vehicle-ids []
           :focused-route nil
           :focused-route-fit-key nil
           :route-loading? true
           :route-loading-transport-id (:transport-id payload))

    :route/start-selection
    (assoc state
           :focused-transport-id nil
           :focused-selection-vehicle-ids (vec (:vehicle-ids payload))
           :focused-route nil
           :focused-route-fit-key nil
           :route-loading? false
           :route-loading-transport-id nil)

    :route/set-focused-id
    (assoc state
           :focused-transport-id (:transport-id payload)
           :focused-selection-vehicle-ids [])

    :route/result
    (-> state
        (assoc :focused-route (:route payload))
        (assoc :route-loading? false)
        (assoc :route-loading-transport-id nil)
        (assoc :focused-route-fit-key (:fit-key payload)))

    :popup/pending-assoc
    (assoc-in state [:pending-ops (:op-id payload)] (:op-data payload))

    :popup/pending-update
    (update-in state [:pending-ops (:op-id payload)] (or (:f payload) identity))

    :popup/pending-rewrite
    (update state :pending-ops (or (:f payload) identity))

    :popup/pending-dissoc
    (update state :pending-ops dissoc (:op-id payload))

    :transport/sync-from-loads
    (let [server-assignments (lb2sel/loads->assignment (:loads payload) (:fahrzeuge payload))
          server-assigned-vehicle-ids (lb2sel/assignments->assigned-ids server-assignments)
          valid-vehicle-ids (some->> (:fahrzeuge payload) (keep :ductile/id) set)
          keep-vehicle-id? (if (seq valid-vehicle-ids)
                             #(contains? valid-vehicle-ids %)
                             (constantly true))
          prune-local-vehicle-ids (fn [vehicle-ids]
                                    (->> vehicle-ids
                                         (remove server-assigned-vehicle-ids)
                                         (distinct-filtered-vec keep-vehicle-id?)))
          draft-ids (set (keys (:draft-transports state)))
          draft-assignments (update-vals
                             (select-keys (:transport->vehicle-ids state) draft-ids)
                             prune-local-vehicle-ids)
          prev-assignments (:transport->vehicle-ids state)
          server-assignments (update-vals server-assignments
                                          #(distinct-filtered-vec keep-vehicle-id? %))
          transport->vehicle-ids (merge server-assignments draft-assignments)
          load-signatures (loads->signatures (:loads payload))
          prev-load-signatures (:server-load-signatures state)
          changed-persisted-ids (into #{}
                                      (keep (fn [transport-id]
                                              (when (or (not= (get prev-assignments transport-id)
                                                              (get transport->vehicle-ids transport-id))
                                                        (not= (get prev-load-signatures transport-id)
                                                              (get load-signatures transport-id)))
                                                transport-id)))
                                      (keys server-assignments))
          changed-draft-ids (into #{}
                                  (keep (fn [draft-id]
                                          (when (not= (get prev-assignments draft-id)
                                                      (get draft-assignments draft-id))
                                            draft-id)))
                                  draft-ids)
          changed-transport-ids (into changed-persisted-ids changed-draft-ids)
          valid-transport-ids (set (keys transport->vehicle-ids))
          selected-vehicle-ids (set (distinct-filtered-vec keep-vehicle-id?
                                                           (:selected-vehicle-ids state)))
          focused-selection-vehicle-ids (distinct-filtered-vec keep-vehicle-id?
                                                               (:focused-selection-vehicle-ids state))
          selection-anchor-id (when (contains? selected-vehicle-ids (:selection-anchor-id state))
                                (:selection-anchor-id state))
          focused-transport-id (when (contains? valid-transport-ids (:focused-transport-id state))
                                 (:focused-transport-id state))
          route-loading-transport-id (when (contains? valid-transport-ids (:route-loading-transport-id state))
                                       (:route-loading-transport-id state))
          focused-selection-emptied? (and (seq (:focused-selection-vehicle-ids state))
                                          (empty? focused-selection-vehicle-ids))
          focused-transport-removed? (and (:focused-transport-id state)
                                          (nil? focused-transport-id))
          route-invalid? (or focused-selection-emptied?
                             focused-transport-removed?
                             (and (:route-loading-transport-id state)
                                  (nil? route-loading-transport-id)))]
      (cond-> (assoc state
                     :transport->vehicle-ids transport->vehicle-ids
                     :transport->waypoint-settings (select-keys (:transport->waypoint-settings state)
                                                                valid-transport-ids)
                     :staging-ids (->> (:staging-ids state)
                                       (remove server-assigned-vehicle-ids)
                                       (distinct-filtered-vec keep-vehicle-id?))
                     :selected-vehicle-ids selected-vehicle-ids
                     :selection-anchor-id selection-anchor-id
                     :focused-selection-vehicle-ids focused-selection-vehicle-ids
                     :focused-transport-id focused-transport-id
                     :route-loading-transport-id route-loading-transport-id
                     :route-loading? (and (:route-loading? state)
                                          (some? route-loading-transport-id))
                     :transport-update-tokens
                     (let [tokens (select-keys (:transport-update-tokens state) valid-transport-ids)]
                       (reduce (fn [acc transport-id]
                                 (update acc transport-id (fnil inc 0)))
                               tokens
                               changed-transport-ids))
                     :server-load-signatures load-signatures
                     :editing-transport-id (when (contains? valid-transport-ids (:editing-transport-id state))
                                             (:editing-transport-id state)))
        route-invalid?
        (assoc :focused-route nil
               :focused-route-fit-key nil
               :route-loading? false)))

    state))
